import { IBusStopData } from "@/domain/entities/busStop";
import { BusStopMarker } from "@/types";
import { ref } from "vue";

interface CreateBusStopMarkerParams {
  naverMap: any;
  lat: number;
  lng: number;
  zIndex: number;
  title: string;
  /** 정류장 마커로 사용될 아이콘의 경로 */
  iconUrl?: any;
  /** 클릭 이벤트 핸들러 */
  onClickHandler?: () => void;
}

/** 네이버 지도 - 버스 정류장 마커 생성 */
export const useCreateBusStopMarkers = () => {
  const busStopMarkers = ref<Array<BusStopMarker>>([]);

  const createBusStopMarkers = (
    naverMap: any,
    busStops?: IBusStopData[],
    busStopMarkerIconUrl = require("@/assets/icons/pin_green.svg"),
    onBusStopClick?: (busStop: IBusStopData) => void
  ) => {
    if (!naverMap || !busStops) return;

    const pinRedUrl = require("@/assets/icons/pin_red.svg");

    busStops.forEach((busStop, index) => {
      const iconUrl = busStop.isOmitted ? pinRedUrl : busStopMarkerIconUrl;
      const title = busStop.isOmitted
        ? "X"
        : getBusStopIconTitle(index + 1, busStops.length);

      const marker = createBusStopMarker({
        naverMap,
        lat: busStop.latitude,
        lng: busStop.longitude,
        zIndex: 100,
        title,
        iconUrl,
        onClickHandler: onBusStopClick
          ? () => onBusStopClick(busStop)
          : undefined,
      });

      busStopMarkers.value.push({ busStop, marker, iconUrl, title });
      marker.setMap(naverMap);
    });
  };

  /** 정류장 마커 등록 */
  const createBusStopMarker = ({
    naverMap,
    lat,
    lng,
    zIndex,
    title,
    iconUrl = require("@/assets/icons/pin_green.svg"),
    onClickHandler,
  }: CreateBusStopMarkerParams) => {
    const position = new window.naver.maps.LatLng(lat, lng);
    const markerOptions = {
      position: position,
      map: naverMap,
      icon: {
        content: getBusStopIconContents(title, iconUrl),
        size: new window.naver.maps.Size(34, 34),
        anchor: new window.naver.maps.Point(34 / 2, 34),
      },
      zIndex: zIndex,
    };
    const marker = new window.naver.maps.Marker(markerOptions);

    // 클릭 이벤트 추가
    if (onClickHandler) {
      window.naver.maps.Event.addListener(marker, "click", onClickHandler);
    }

    return marker;
  };

  const updateSelectedBusStopMarker = (
    selectedBusStopId?: string,
    showAllNames = false
  ) => {
    if (!selectedBusStopId && !showAllNames) return;

    busStopMarkers.value.forEach((busStopMaker, index) => {
      const marker = busStopMaker.marker;
      const busStop = busStopMaker.busStop;

      const iconUrl =
        busStopMaker.iconUrl ?? require("@/assets/icons/pin_green.svg");
      const title =
        busStopMaker.title ??
        getBusStopIconTitle(index + 1, busStopMarkers.value.length);

      const isSelectedBusStop = busStop.id === selectedBusStopId;
      const shouldShowName = showAllNames || isSelectedBusStop;

      const content = getBusStopIconContents(
        title,
        iconUrl,
        shouldShowName ? busStop.name : undefined,
        false // MapOfLineBusStops에서는 기존 스타일(검은 배경) 유지
      );
      const zIndex = isSelectedBusStop ? 101 : 100;

      marker.setIcon({
        content,
        size: new window.naver.maps.Size(34, 34),
        anchor: new window.naver.maps.Point(34 / 2, 34),
      });
      marker.setZIndex(zIndex);
    });
  };

  const updateSelectedBusStopMarkerWithInfoWindow = (
    selectedBusStopId?: string,
    showAllNames = false
  ) => {
    busStopMarkers.value.forEach((busStopMaker, index) => {
      const marker = busStopMaker.marker;
      const busStop = busStopMaker.busStop;

      const iconUrl =
        busStopMaker.iconUrl ?? require("@/assets/icons/pin_green.svg");
      const title =
        busStopMaker.title ??
        getBusStopIconTitle(index + 1, busStopMarkers.value.length);

      const isSelectedBusStop = busStop.id === selectedBusStopId;
      const shouldShowName = showAllNames || isSelectedBusStop;

      const content = getBusStopIconContents(
        title,
        iconUrl,
        shouldShowName ? busStop.name : undefined,
        shouldShowName // LineTurnPathMap에서는 infoWindow 스타일 사용
      );
      const zIndex = isSelectedBusStop ? 101 : 100;

      marker.setIcon({
        content,
        size: new window.naver.maps.Size(34, 34),
        anchor: new window.naver.maps.Point(34 / 2, 34),
      });
      marker.setZIndex(zIndex);
    });
  };

  const resetBusStopMarkers = () => {
    busStopMarkers.value.forEach((busStopMarker) => {
      busStopMarker.marker.setMap(null);
    });

    busStopMarkers.value = [];
  };

  return {
    busStopMarkers,
    createBusStopMarkers,
    createBusStopMarker,
    updateSelectedBusStopMarker,
    updateSelectedBusStopMarkerWithInfoWindow,
    resetBusStopMarkers,
    getBusStopIconContents,
    getBusStopInfoWindowContents,
  };
};

/**
 * 버스 정류장 아이콘에 표시할 텍스트 생성
 * 첫번째 정류장 : "S"
 * 마지막 정류장 : "E"
 * 그 외 : 숫자
 */
function getBusStopIconTitle(index: number, length: number): string {
  if (index === 1) return "S";
  if (index === length) return "E";
  return index.toString();
}

/** 버스 정류장 아이콘 Content(img, title...) 생성 */
function getBusStopIconContents(
  title: string,
  url = require("@/assets/icons/pin_green.svg"),
  label?: string,
  useInfoWindowStyle = false
) {
  const imageUrl = url;

  const contentsHtmlStr = `<div aria-label="${
    title === "X" ? "미경유 정류장" : title
  }" style="
          position: relative;  
          display: flex;
          flex-direction: column;
          align-items: center;
          "
        >
        <div
          style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 34px; height: 34px;
          "
        >
            <img src="${imageUrl}" alt="${title}" />
            <span
              style="
                position: absolute;
                font-size: 14px;
                font-weight: bold;
                color: #fff;
                margin-bottom: 3px;
              "
              >${title}</span
            >
        </div>
        ${
          label
            ? useInfoWindowStyle
              ? getBusStopInfoWindowContents(label)
              : `<p style="
                  position: absolute;
                  bottom: -4px;
                  transform: translateY(100%);
                  padding: 4px; 
                  border-radius: 2px; 
                  font-size: 13px; 
                  font-weight: bold; 
                  color: #fff; 
                  background-color: #000000b3;
                  white-space: nowrap;
                  "
                >${label}</p>`
            : ""
        }
      </div>`;
  return contentsHtmlStr;
}

/** infoWindow 스타일의 말풍선 생성 */
function getBusStopInfoWindowContents(label: string) {
  return `<div style="
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translate(-50%, -100%);
    background-color: #fff;
    border: 1px solid #707070;
    border-radius: 4px;
    padding: 7px 9px;
    font-size: 14px;
    font-weight: normal;
    color: #000;
    white-space: nowrap;
    z-index: 1000;
  ">
    ${label}
    <div style="
      position: absolute;
      bottom: -7px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 7px solid #707070;
    "></div>
    <div style="
      position: absolute;
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #fff;
    "></div>
  </div>`;
}
